import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Check, Crown, Loader2, LogOut, Gift, Sparkles } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { ACCENT, planPrice, BillingToggle, PlanComparison } from "../components/plans";
import { BubzLogo } from "../components/BubzLogo";

export default function ChoosePlan() {
  const navigate = useNavigate();
  const { subscription, refreshSubscription, logout } = useAuth();
  const [plans, setPlans] = useState([]);
  const [cycle, setCycle] = useState("monthly");
  const [busy, setBusy] = useState(null);
  const [code, setCode] = useState("");
  const [redeeming, setRedeeming] = useState(false);

  useEffect(() => { api.get("/subscription/plans").then(({ data }) => setPlans(data)); }, []);
  useEffect(() => { if (subscription) navigate("/profiles", { replace: true }); }, [subscription, navigate]);

  const choose = async (plan) => {
    setBusy(plan.id);
    try {
      const { data } = await api.post("/subscription/checkout", { plan: plan.id, cycle, origin_url: window.location.origin });
      if (data.demo) {
        toast.success("Demo tier activated 🎬");
        await refreshSubscription();
        navigate("/profiles", { replace: true });
      } else if (data.checkout_url) {
        window.location.href = data.checkout_url;
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(null); }
  };

  const redeem = async () => {
    if (!code.trim()) return;
    setRedeeming(true);
    try {
      const { data } = await api.post("/gifts/redeem", { code });
      toast.success(`${data.plan_name} unlocked for ${data.months} month${data.months > 1 ? "s" : ""}! 🎁`);
      await refreshSubscription();
      navigate("/profiles", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setRedeeming(false); }
  };

  return (
    <div className="min-h-screen bg-[#FFFDF7]">
      <div className="max-w-6xl mx-auto px-6 py-12">
        <div className="flex items-center justify-between">
          <div className="text-3xl font-display font-bold text-[#FF5A79]"><BubzLogo size="md" /></div>
          <button onClick={logout} data-testid="plan-logout-btn" className="flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-slate-800">
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>

        <div className="text-center mt-10">
          <span className="inline-flex items-center gap-2 rounded-full bg-[#9B5DE5]/10 text-[#9B5DE5] px-4 py-1.5 text-sm font-bold"><Crown className="h-4 w-4" /> Choose your plan to start streaming</span>
          <h1 className="mt-4 text-4xl sm:text-5xl font-display font-bold text-slate-900">Pick a Bubz + plan</h1>
          <p className="text-slate-500 mt-3 max-w-xl mx-auto">Unlimited kids shows, learning games and more. Paid plans start with a 7-day free trial. Cancel anytime.</p>
          <div className="mt-6 flex justify-center"><BillingToggle cycle={cycle} setCycle={setCycle} /></div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-10" data-testid="choose-plan-grid">
          {plans.map((p, i) => {
            const accent = ACCENT[p.id] || "#FF5A79";
            const pr = planPrice(p, cycle);
            const paid = p.price > 0;
            return (
              <motion.div key={p.id} data-testid={`choose-plan-${p.id}`}
                initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}
                className={`rounded-3xl border-2 p-6 flex flex-col bg-white ${p.id === "standard" ? "shadow-xl" : "shadow-lg"}`}
                style={{ borderColor: p.id === "standard" ? accent : "#f1e9d9" }}>
                <div className="flex items-center gap-2 flex-wrap">
                  {p.id === "standard" && <span className="rounded-full bg-[#FF5A79] text-white px-3 py-0.5 text-xs font-bold">Most popular</span>}
                  {paid && <span className="rounded-full bg-emerald-100 text-emerald-700 px-2.5 py-0.5 text-xs font-bold">7-day free trial</span>}
                </div>
                <div className="font-display text-2xl font-bold text-slate-900 mt-2">{p.name}</div>
                <div className="mt-1">
                  {pr.label === "Free" ? <span className="text-3xl font-display font-bold" style={{ color: accent }}>Free</span>
                    : <><span className="text-4xl font-display font-bold text-slate-900">{pr.label}</span><span className="text-slate-400">{pr.suffix}</span></>}
                </div>
                {pr.note && <div className="text-xs font-semibold text-emerald-600 mt-0.5">{pr.note}</div>}
                <div className="text-xs font-semibold text-slate-400 mt-1">{p.quality} · {p.devices}</div>
                <ul className="mt-4 space-y-2 flex-1">
                  {p.features.map((f, j) => (
                    <li key={j} className="flex items-start gap-2 text-sm text-slate-600"><Check className="h-4 w-4 mt-0.5 shrink-0" style={{ color: accent }} /> {f}</li>
                  ))}
                </ul>
                <Button data-testid={`choose-subscribe-${p.id}`} disabled={busy === p.id} onClick={() => choose(p)}
                  className="mt-6 w-full h-12 rounded-xl font-bold text-white" style={{ backgroundColor: accent }}>
                  {busy === p.id ? <Loader2 className="h-4 w-4 animate-spin" /> : p.id === "demo" ? "Start free" : "Start free trial"}
                </Button>
              </motion.div>
            );
          })}
        </div>

        {/* Gift redemption */}
        <div className="mt-10 rounded-3xl border-2 border-[#9B5DE5]/30 bg-[#9B5DE5]/5 p-6 flex flex-col sm:flex-row sm:items-center gap-4" data-testid="redeem-gift-box">
          <span className="h-12 w-12 rounded-2xl bg-[#9B5DE5] text-white flex items-center justify-center shrink-0"><Gift className="h-6 w-6" /></span>
          <div className="flex-1">
            <h3 className="font-display text-lg font-bold text-slate-900">Have a gift code?</h3>
            <p className="text-sm text-slate-500">Redeem it to unlock Bubz + instantly — no card needed.</p>
          </div>
          <div className="flex gap-2 w-full sm:w-auto">
            <Input data-testid="redeem-code-input" value={code} onChange={(e) => setCode(e.target.value)} placeholder="BUBZ-XXXX-XXXX-XXXX"
              className="rounded-xl h-11 font-mono tracking-wide uppercase sm:w-64" />
            <Button data-testid="redeem-code-btn" onClick={redeem} disabled={redeeming || !code.trim()}
              className="rounded-xl h-11 px-5 font-bold bg-[#9B5DE5] hover:bg-[#8349c9] text-white">
              {redeeming ? <Loader2 className="h-4 w-4 animate-spin" /> : "Redeem"}
            </Button>
          </div>
        </div>

        {/* Comparison */}
        <div className="mt-12">
          <h2 className="text-2xl font-display font-bold text-slate-900 text-center mb-6">Compare every plan</h2>
          <PlanComparison plans={plans} cycle={cycle} />
        </div>

        <p className="text-center text-xs text-slate-400 mt-6 flex items-center justify-center gap-1"><Sparkles className="h-3 w-3" /> Paid plans use Stripe test mode — card 4242 4242 4242 4242.</p>
      </div>
    </div>
  );
}
